/**
 * metaPageTracking.js
 *
 * ✅ This is the ONLY file that pages should import for Meta tracking.
 *
 * Each function maps to one specific user action:
 *
 *   App.jsx startup         → initializeMetaPixelOnAppLoad()
 *   Landing page opened     → trackLandingPageVisit(pixelId)
 *   Lead created            → trackLeadEvent(product)
 *   Order success page      → trackOrderSuccessPurchase(orderData)
 *   Fallback payment done   → trackFallbackPaymentSuccess(orderData)
 *   Debugging               → debugMetaTracking()
 *
 * Do NOT call metaConversionsHelper or metaTrackingConfig directly from pages.
 */

import metaConfig from './metaTrackingConfig';
import { captureAndStoreFbclid } from './fbclid';
import {
  initPixel,
  firePixelEvent,
  sendCAPIEvent,
} from './metaConversionsHelper';

// ─── App startup ─────────────────────────────────────────────────────────────

/**
 * Call once in App.jsx on mount.
 * Waits for DB config, then initialises the Pixel if enabled.
 */
export const initializeMetaPixelOnAppLoad = async () => {
  try {
    await metaConfig.ready();
    if (!metaConfig.isTrackingEnabled()) return { skipped: true };

    initPixel(metaConfig.getPixelId());
    firePixelEvent('PageView');
    return { success: true };
  } catch (err) {
    console.warn('[MetaTracking] App init failed (non-blocking):', err);
    return { success: false };
  }
};

// ─── Landing page ─────────────────────────────────────────────────────────────

/**
 * Call at the top of LandingPage on mount.
 * Captures fbclid from the URL and initialises the Pixel.
 * @param {string} pixelId  The hardcoded pixel ID used on the landing page.
 */
export const trackLandingPageVisit = (pixelId) => {
  captureAndStoreFbclid();   // store ?fbclid=… → _fbc cookie / localStorage
  initPixel(pixelId);
  firePixelEvent('PageView');
};

/**
 * Call when a lead is created (form submitted).
 * @param {{ name?: string, price?: number }} product
 */
export const trackLeadEvent = (product = {}) => {
  return firePixelEvent('Lead', {
    content_name:     product.name  || 'Astrology Service',
    content_category: 'Astrology',
    value:            Number(product.price) || 0,
    currency:         'INR',
  });
};

// ─── Purchases ────────────────────────────────────────────────────────────────

/**
 * Call on OrderSuccess page when an order is confirmed.
 * Fires both Pixel (client) + Conversions API (server) for maximum accuracy.
 */
export const trackOrderSuccessPurchase = async (orderData) => {
  await metaConfig.ready();
  if (!metaConfig.isPageTrackingEnabled('orderSuccess')) return { skipped: true };

  // Client-side Pixel event
  firePixelEvent('Purchase', {
    value:    parseFloat(orderData.amount   || 0),
    currency: orderData.currency || 'INR',
  });

  // Server-side Conversions API event
  return sendCAPIEvent('Purchase', orderData);
};

/**
 * Call from FallbackPaymentModal when QR / fallback payment is confirmed.
 * Sends only via Conversions API (server-side) since Pixel may not be active.
 */
export const trackFallbackPaymentSuccess = async (orderData) => {
  await metaConfig.ready();
  if (!metaConfig.isPageTrackingEnabled('fallbackPayment')) return { skipped: true };
  return sendCAPIEvent('Purchase', orderData);
};

// ─── Deprecated alias (kept for backward compatibility) ───────────────────────
/** @deprecated Use trackLeadEvent() instead */
export const trackLandingPageLead = (leadData = {}) =>
  trackLeadEvent({ name: leadData.service, price: leadData.estimatedValue });

// ─── Debug ────────────────────────────────────────────────────────────────────

/** Logs and returns current tracking state — useful for debugging. */
export const debugMetaTracking = () => {
  const info = {
    ...metaConfig.getDebugInfo(),
    fbPixelLoaded: !!window.fbq,
    currentPage:   window.location.pathname,
  };
  console.log('[MetaTracking] Debug info:', info);
  return info;
};

export default {
  initializeMetaPixelOnAppLoad,
  trackLandingPageVisit,
  trackLeadEvent,
  trackOrderSuccessPurchase,
  trackFallbackPaymentSuccess,
  debugMetaTracking,
};